import { useQuery } from '@apollo/client'
import { sum } from 'lodash'
import { useMemo } from 'react'
import { useBasketItems } from './useBasketItems/useBasketItems'
import { BasketOutletMenuItemsDocument } from './useTotals/queries/__generated__/BasketOutletMenuItems.graphql-interface'
import { mergeBasketItemsWithPrices } from './useTotals/utils/mergeBasketItemsWithPrices'
import { AsyncDataWrapper } from './types'
import { BasketItemWithPrices } from './useTotals/useBasketTotals'

export type UseBasketItemTotalsResponseData = {
  basketItemsWithPrices: BasketItemWithPrices[]
  nonAddOnBasketItemsTotal: number
  addOnBasketItemsTotal: number
  basketItemsTotal: number
}

// hook which provides a total of the basket items, ignoring fulfilments and discounts
export const useBasketItemTotals =
  (): AsyncDataWrapper<UseBasketItemTotalsResponseData> => {
    const { items, uniqueOutletMenuItemIds } = useBasketItems()

    const skip = !uniqueOutletMenuItemIds.length

    // get the prices for the non add on items
    const basketOutletMenuItemsQueryResponse = useQuery(
      BasketOutletMenuItemsDocument,
      {
        variables: {
          outletMenuItemIds: uniqueOutletMenuItemIds,
        },
        skip,
      }
    )
    const {
      data: basketOutletMenuItemCurrentData,
      previousData: basketOutletMenuItemPreviousData,
    } = basketOutletMenuItemsQueryResponse

    // calculate the prices of all the items in the basket
    const basketItemsWithPricesById = useMemo(() => {
      const basketOutletMenuItemData =
        basketOutletMenuItemCurrentData || basketOutletMenuItemPreviousData
      if (!basketOutletMenuItemData) {
        return {}
      }
      return mergeBasketItemsWithPrices({
        basketItems: items,
        basketOutletMenuItems: basketOutletMenuItemData.outletMenuItems,
      })
    }, [
      items,
      basketOutletMenuItemCurrentData,
      basketOutletMenuItemPreviousData,
    ])

    // no query, no results
    if (skip) {
      return {
        data: {
          basketItemsWithPrices: [],
          nonAddOnBasketItemsTotal: 0,
          addOnBasketItemsTotal: 0,
          basketItemsTotal: 0,
        },
        loading: false,
        error: null,
      }
    }

    const basketItemsWithPrices = Object.values(basketItemsWithPricesById)

    // calculate the non-add on items total
    const nonAddOnBasketItemsTotal = sum(
      basketItemsWithPrices
        .filter(item => !item.isAddOnItem)
        .map(item => item.totalPrice)
    )

    // calculate the add on items total
    const addOnBasketItemsTotal = sum(
      basketItemsWithPrices
        .filter(item => item.isAddOnItem)
        .map(item => item.totalPrice)
    )

    // return the data, unless there's an error, in which case return the error and null
    return !basketOutletMenuItemsQueryResponse.error
      ? {
          data: {
            basketItemsWithPrices,
            nonAddOnBasketItemsTotal,
            addOnBasketItemsTotal,
            basketItemsTotal: nonAddOnBasketItemsTotal + addOnBasketItemsTotal,
          },
          loading: basketOutletMenuItemsQueryResponse.loading,
          error: null,
        }
      : {
          data: null,
          loading: false,
          error: basketOutletMenuItemsQueryResponse.error,
        }
  }
